import { fetchUtils } from 'react-admin';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
const stepUrl = `${apiUrl}/QmsWorkflowStep`;
const templateUrl = `${apiUrl}/QmsWorkflowTemplate`;
interface ApprovalStep {
  id?: string;
  stepNo: number;
  Name: string;
  stepType: 'single' | 'parallel' | 'conditional';
  approvers: any[];
  approvalRule: 'all' | 'any' | 'majority';
  timeLimit?: number;
  description: string;
  isOptional: boolean;
  instanceId?: string;
}

interface ApprovalTemplate {
  id?: string;
  Name: string;
  docType: string;
  description: string;
  steps: ApprovalStep[];
  isActive: boolean;
  createdDate: string;
  createdBy: number;
  lastModified: string;
  usageCount: number;
  stepsLoaded?: boolean;
}

function getHeaders() {
  const headers = new Headers({ Accept: 'application/json' });
  const authToken = localStorage.getItem('session') || localStorage.getItem('token');
  if (authToken) headers.set('Authorization', `Bearer ${authToken}`);
  return headers;
}

function getCurrentUserId(): number | null {
  const raw = localStorage.getItem('userId');
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
}

function isDepartmentSelector(item: any): boolean {
  if (!item) return false;
  if (typeof item === 'string') return item.startsWith('dept:') || item.startsWith('deptid:');
  if (typeof item === 'object') {
    const id = item.id != null ? String(item.id) : '';
    return item.selectorType === 'departmentTitle' || id.startsWith('dept:') || id.startsWith('deptid:');
  }
  return false;
}

function extractApproverIds(approvers: any[]): Array<string | number> {
  if (!Array.isArray(approvers)) return [];
  return approvers
    .map(item => {
      if (item == null) return undefined;
      if (typeof item === 'string' || typeof item === 'number') {
        const normalized = String(item);
        if (!normalized.trim()) return undefined;
        return normalized.startsWith('dept:') || normalized.startsWith('deptid:') ? undefined : item;
      }
      if (typeof item === 'object') {
        const id = (item as any).id;
        const userId = (item as any).userId;
        const normalizedUserId = userId != null ? String(userId).trim() : '';
        const normalizedId = id != null ? String(id).trim() : '';
        const candidate = normalizedUserId || normalizedId;
        if (!candidate) return undefined;
        const normalized = String(candidate);
        return normalized.startsWith('dept:') || normalized.startsWith('deptid:') ? undefined : candidate;
      }
      return undefined;
    })
    .filter((id): id is string | number => id !== undefined && id !== null && String(id).trim() !== '');
}

function extractDepartmentTitles(approvers: any[], fallbackTitles?: any): string[] {
  const titles = new Set<string>();

  if (Array.isArray(fallbackTitles)) {
    fallbackTitles.forEach((title: any) => {
      const normalized = String(title || '').trim();
      if (normalized) titles.add(normalized);
    });
  }

  if (!Array.isArray(approvers)) return Array.from(titles);

  approvers.forEach(item => {
    if (item == null) return;

    if (typeof item === 'string') {
      if (item.startsWith('dept:')) {
        const title = item.replace(/^dept:/, '').trim();
        if (title) titles.add(title);
      } else if (item.startsWith('deptid:')) {
        // deptid selector keeps title in fallbackTitles, so do nothing here
      }
      return;
    }

    if (typeof item === 'object') {
      const rawTitle =
        (item as any).selectorType === 'departmentTitle'
          ? ((item as any).selectorValue || (item as any).departmentTitle || (item as any).name)
          : ((item as any).departmentTitle || (item as any).departmentTile);
      const normalized = String(rawTitle || '').trim();
      if (normalized && isDepartmentSelector(item)) {
        titles.add(normalized);
      }
    }
  });

  return Array.from(titles);
}

function extractDepartmentTitleIds(approvers: any[], fallbackIds?: any): Array<string | number> {
  const ids = new Set<string | number>();

  if (Array.isArray(fallbackIds)) {
    fallbackIds.forEach((id: any) => {
      if (id !== undefined && id !== null && String(id).trim() !== '') {
        ids.add(typeof id === 'number' ? id : String(id));
      }
    });
  }

  if (!Array.isArray(approvers)) return Array.from(ids);

  approvers.forEach(item => {
    if (item == null) return;

    if (typeof item === 'string') {
      if (item.startsWith('deptid:')) {
        const rawId = item.replace(/^deptid:/, '').trim();
        if (rawId) ids.add(rawId);
      }
      return;
    }

    if (typeof item === 'object' && isDepartmentSelector(item)) {
      const rawId =
        (item as any).departmentTitleId ??
        (item as any).selectorValue;
      if (rawId !== undefined && rawId !== null && String(rawId).trim() !== '') {
        ids.add(typeof rawId === 'number' ? rawId : String(rawId));
      }
    }
  });

  return Array.from(ids);
}

function createDepartmentSelectors(titles: any[] | undefined, ids?: any[] | undefined): any[] {
  if (!Array.isArray(titles) || titles.length === 0) return [];

  // Handle new API format where departmentTitles is an array of objects
  // e.g. [{id: 77, departmentTitleId: 1, departmentTitle: "QA Manager"}]
  const firstItem = titles[0];
  if (firstItem !== null && typeof firstItem === 'object') {
    return titles
      .map((item: any) => {
        if (item == null) return null;
        const title = String(item.departmentTitle || item.departmentTile || item.name || '').trim();
        const deptId = String(item.departmentTitleId || item.departmentTileId || '').trim();
        if (!title) return null;
        return {
          id: deptId ? `deptid:${deptId}` : `dept:${title}`,
          userId: '',
          name: title,
          role: 'DepartmentTitle',
          email: '',
          departmentTitle: title,
          departmentTitleId: deptId,
          selectorType: 'departmentTitle',
          selectorValue: deptId || title
        };
      })
      .filter(Boolean);
  }

  // Original string-based handling
  const safeTitles = titles.map(title => String(title || '').trim()).filter(Boolean);
  const safeIds = Array.isArray(ids)
    ? ids.map(id => String(id || '').trim())
    : [];

  return safeTitles.map((title, index) => {
    const titleId = safeIds[index] || '';
    return {
      id: titleId ? `deptid:${titleId}` : `dept:${title}`,
      userId: '',
      name: title,
      role: 'DepartmentTitle',
      email: '',
      departmentTitle: title,
      departmentTitleId: titleId,
      selectorType: 'departmentTitle',
      selectorValue: titleId || title
    };
  });
}

/**
 * Get all workflow templates
 */
export async function fetchAllTemplates(options: { includeSteps?: boolean } = {}): Promise<ApprovalTemplate[]> {
  const { includeSteps = true } = options;
  try {
    const response = await fetchUtils.fetchJson(
      `${templateUrl}`,
      { headers: getHeaders() }
    );
    
    const templates = response.json.data || response.json || [];

    if (!includeSteps) {
      return templates.map((template: any) => ({
        ...template,
        Name: template.Name || template.name,
        docType: template.docType || template.DocumentType || '',
        description: template.description || '',
        steps: [],
        stepsLoaded: false
      }));
    }
    
    // Check if templates already include steps data
    // If yes, use it directly; if no, fetch steps separately
    const result = await Promise.all(
      templates.map(async (template: any) => {
        try {
          // Check if template already has steps data
          const existingSteps = template.steps as any[];
          if (Array.isArray(existingSteps) && existingSteps.length > 0) {
            // Steps already included in template, use directly
            return {
              ...template,
              Name: template.Name || template.name,
              description: template.description || '',
              steps: existingSteps.map((step: any, idx: number) => {
                const rawApprovers = step.approvers ?? step.approverIds;
                return {
                  ...step,
                  id: step.id || `s${idx}`,
                  Name: step.Name || step.name || '',
                  stepNo: step.stepNo || idx + 1,
                  approvers: (() => {
                    const parsedApprovers = typeof rawApprovers === 'string'
                      ? JSON.parse(rawApprovers)
                      : (Array.isArray(rawApprovers) ? rawApprovers : []);
                    const departmentSelectors = createDepartmentSelectors(
                      step.departmentTitles,
                      step.departmentTitleIds || step.DepartmentTitleIds
                    );
                    const merged = [...parsedApprovers, ...departmentSelectors];
                    return merged.filter((item: any, index: number, arr: any[]) => {
                      const key = typeof item === 'object' ? `${item?.id ?? ''}-${item?.selectorType ?? ''}` : String(item);
                      return arr.findIndex((target: any) => {
                        const targetKey = typeof target === 'object' ? `${target?.id ?? ''}-${target?.selectorType ?? ''}` : String(target);
                        return targetKey === key;
                      }) === index;
                    });
                  })()
                };
              }),
              stepsLoaded: true
            };
          }
          
          // If no steps in template, fetch from separate API
          const stepsResponse = await fetchUtils.fetchJson(
            `${stepUrl}/byTemplate/${template.id}`,
            { headers: getHeaders() }
          );
          const steps = stepsResponse.json.data || stepsResponse.json || [];
          
          return {
            ...template,
            Name: template.Name || template.name,
            description: template.description || '',
            steps: steps.map((step: any, idx: number) => {
              const rawApprovers = step.approvers ?? step.approverIds;
              return {
                ...step,
                id: step.id || `s${idx}`,
                Name: step.Name || step.name || '',
                stepNo: step.stepNo || idx + 1,
                approvers: (() => {
                  const parsedApprovers = typeof rawApprovers === 'string'
                    ? JSON.parse(rawApprovers)
                    : (Array.isArray(rawApprovers) ? rawApprovers : []);
                  const departmentSelectors = createDepartmentSelectors(
                    step.departmentTitles,
                    step.departmentTitleIds || step.DepartmentTitleIds
                  );
                  const merged = [...parsedApprovers, ...departmentSelectors];
                  return merged.filter((item: any, index: number, arr: any[]) => {
                    const key = typeof item === 'object' ? `${item?.id ?? ''}-${item?.selectorType ?? ''}` : String(item);
                    return arr.findIndex((target: any) => {
                      const targetKey = typeof target === 'object' ? `${target?.id ?? ''}-${target?.selectorType ?? ''}` : String(target);
                      return targetKey === key;
                    }) === index;
                  });
                })()
              };
            }),
            stepsLoaded: true
          };
        } catch (error) {
          console.error(`Failed to fetch steps for template ${template.id}:`, error);
          return { 
            ...template,
            Name: template.Name || template.name,
            docType: template.docType || template.DocumentType || '',
            description: template.description || '',
            steps: [],
            stepsLoaded: false
          };
        }
      })
    );
    
    return result;
  } catch (error) {
    console.error('Error fetching templates:', error);
    throw error;
  }
}

/**
 * Get single template with steps
 */
export async function fetchTemplate(templateId: string): Promise<ApprovalTemplate> {
  try {
    const response = await fetchUtils.fetchJson(
      `${templateUrl}/${templateId}`,
      { headers: getHeaders() }
    );
    
    const template = response.json.data || response.json;
    
    // Check if template already includes steps data
    const existingSteps = template.steps as any[];
    if (Array.isArray(existingSteps) && existingSteps.length > 0) {
      // Steps already included, use directly
      return {
        ...template,
        Name: template.Name || template.name,
        docType: template.docType || template.DocumentType || '',
        description: template.description || '',
        steps: existingSteps.map((step: any, idx: number) => {
          const rawApprovers = step.approvers ?? step.approverIds;
          return {
            ...step,
            id: step.id || `s${idx}`,
            Name: step.Name || step.name || '',
            stepNo: step.stepNo || idx + 1,
            approvers: (() => {
              const parsedApprovers = typeof rawApprovers === 'string'
                ? JSON.parse(rawApprovers)
                : (Array.isArray(rawApprovers) ? rawApprovers : []);
              const departmentSelectors = createDepartmentSelectors(
                step.departmentTitles,
                step.departmentTitleIds || step.DepartmentTitleIds
              );
              const merged = [...parsedApprovers, ...departmentSelectors];
              return merged.filter((item: any, index: number, arr: any[]) => {
                const key = typeof item === 'object' ? `${item?.id ?? ''}-${item?.selectorType ?? ''}` : String(item);
                return arr.findIndex((target: any) => {
                  const targetKey = typeof target === 'object' ? `${target?.id ?? ''}-${target?.selectorType ?? ''}` : String(target);
                  return targetKey === key;
                }) === index;
              });
            })()
          };
        }),
        stepsLoaded: true
      };
    }
    
    // If no steps in template, fetch from separate API
    const stepsResponse = await fetchUtils.fetchJson(
      `${stepUrl}/byTemplate/${templateId}`,
      { headers: getHeaders() }
    );
    
    const steps = stepsResponse.json.data || stepsResponse.json || [];
    
    return {
      ...template,
      Name: template.Name || template.name,
      docType: template.docType || template.DocumentType || '',
      description: template.description || '',
      steps: steps.map((step: any, idx: number) => {
        const rawApprovers = step.approvers ?? step.approverIds;
        return {
          ...step,
          id: step.id || `s${idx}`,
          Name: step.Name || step.name || '',
          stepNo: step.stepNo || idx + 1,
          approvers: (() => {
            const parsedApprovers = typeof rawApprovers === 'string'
              ? JSON.parse(rawApprovers)
              : (Array.isArray(rawApprovers) ? rawApprovers : []);
            const departmentSelectors = createDepartmentSelectors(
              step.departmentTitles,
              step.departmentTitleIds || step.DepartmentTitleIds
            );
            const merged = [...parsedApprovers, ...departmentSelectors];
            return merged.filter((item: any, index: number, arr: any[]) => {
              const key = typeof item === 'object' ? `${item?.id ?? ''}-${item?.selectorType ?? ''}` : String(item);
              return arr.findIndex((target: any) => {
                const targetKey = typeof target === 'object' ? `${target?.id ?? ''}-${target?.selectorType ?? ''}` : String(target);
                return targetKey === key;
              }) === index;
            });
          })()
        };
      }),
      stepsLoaded: true
    };
  } catch (error) {
    console.error('Error fetching template:', error);
    throw error;
  }
}

/**
 * Create new template with steps
 */
export async function createTemplate(template: ApprovalTemplate): Promise<ApprovalTemplate> {
  try {
    // Create template
    const createdByValue = Number.isFinite(Number(template.createdBy))
      ? Number(template.createdBy)
      : (getCurrentUserId() ?? 0);

    const templateData = {
      name: template.Name,
      docType: template.docType,
      description: template.description,
      isActive: template.isActive,
      createdDate: template.createdDate,
      createdBy: createdByValue,
      lastModified: template.lastModified,
      usageCount: template.usageCount
    };

    const templateResponse = await fetchUtils.fetchJson(
      `${templateUrl}`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(templateData)
      }
    );

    const createdTemplate = templateResponse.json.data || templateResponse.json;
    const templateId = createdTemplate.id;

    // Create steps
    if (template.steps && template.steps.length > 0) {
      await Promise.all(
        template.steps.map(step =>
          fetchUtils.fetchJson(
            `${stepUrl}`,
            {
              method: 'POST',
              headers: getHeaders(),
              body: JSON.stringify({
                  TemplateId: templateId,
                  stepNo: step.stepNo,
                  Name: step.Name,
                  stepType: step.stepType,
                  approverIds: extractApproverIds(step.approvers || []),
                  departmentTitles: extractDepartmentTitles(step.approvers || [], (step as any).departmentTitles),
                  departmentTitleIds: extractDepartmentTitleIds(step.approvers || [], (step as any).departmentTitleIds || (step as any).DepartmentTitleIds),
                  DepartmentTitleIds: extractDepartmentTitleIds(step.approvers || [], (step as any).departmentTitleIds || (step as any).DepartmentTitleIds),
                  approvers: step.approvers || [],
                  approvalRule: step.approvalRule,
                  timeLimit: step.timeLimit || 0,
                  description: step.description,
                  isOptional: step.isOptional
                  // instanceId: step.instanceId || ''
                }
              )
            }
          )
        )
      );
    }

    // Return the newly created template with steps
    return {
      ...createdTemplate,
      Name: createdTemplate.Name || createdTemplate.name,
      docType: createdTemplate.docType || createdTemplate.DocumentType || '',
      description: createdTemplate.description || '',
      steps: template.steps || [],
      isActive: template.isActive,
      createdDate: template.createdDate,
      createdBy: createdByValue,
      lastModified: template.lastModified,
      usageCount: template.usageCount
    };
  } catch (error) {
    console.error('Error creating template:', error);
    throw error;
  }
}

/**
 * Update template with steps
 */
export async function updateTemplate(template: ApprovalTemplate): Promise<ApprovalTemplate> {
  try {
    // Update template
    const createdByValue = Number.isFinite(Number(template.createdBy))
      ? Number(template.createdBy)
      : (getCurrentUserId() ?? 0);

    const templateData = {
      name: template.Name,
      docType: template.docType,
      description: template.description,
      isActive: template.isActive,
      createdDate: template.createdDate,
      createdBy: createdByValue,
      lastModified: new Date().toISOString().split('T')[0],
      usageCount: template.usageCount
    };

    await fetchUtils.fetchJson(
      `${templateUrl}/${template.id}`,
      {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(templateData)
      }
    );

    // Get existing steps from database
    const stepsResponse = await fetchUtils.fetchJson(
      `${stepUrl}/byTemplate/${template.id}`,
      { headers: getHeaders() }
    );
    
    const existingSteps = stepsResponse.json.data || stepsResponse.json || [];

    // Process steps: distinguish between new steps (with temp id like 's1') and existing steps (with numeric id)
    const stepsToCreate = template.steps?.filter(s => s.id && typeof s.id === 'string' && s.id.startsWith('s')) || [];
    const stepsToUpdate = template.steps?.filter(s => s.id && !String(s.id).startsWith('s')) || [];
    const newStepIds = new Set(stepsToUpdate.map(s => s.id));

    // Delete steps that were removed
    const stepsToDelete = existingSteps.filter((s: ApprovalStep) => !newStepIds.has(s.id));
    await Promise.all(
      stepsToDelete.map((step: ApprovalStep) =>
        fetchUtils.fetchJson(
          `${stepUrl}/${step.id}`,
          {
            method: 'DELETE',
            headers: getHeaders()
          }
        )
      )
    );

    // Update existing steps
    await Promise.all(
      stepsToUpdate.map(step =>
        fetchUtils.fetchJson(
          `${stepUrl}/${step.id}`,
          {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({
              stepNo: step.stepNo,
              Name: step.Name,
              stepType: step.stepType,
              approverIds: extractApproverIds(step.approvers || []),
              departmentTitles: extractDepartmentTitles(step.approvers || [], (step as any).departmentTitles),
              departmentTitleIds: extractDepartmentTitleIds(step.approvers || [], (step as any).departmentTitleIds || (step as any).DepartmentTitleIds),
              DepartmentTitleIds: extractDepartmentTitleIds(step.approvers || [], (step as any).departmentTitleIds || (step as any).DepartmentTitleIds),
              approvers: step.approvers || [],
              approvalRule: step.approvalRule,
              timeLimit: step.timeLimit || 0,
              description: step.description,
              isOptional: step.isOptional
            })
          }
        )
      )
    );

    // Create new steps
    if (stepsToCreate.length > 0) {
      await Promise.all(
        stepsToCreate.map(step =>
          fetchUtils.fetchJson(
            `${stepUrl}`,
            {
              method: 'POST',
              headers: getHeaders(),
              body: JSON.stringify({
                TemplateId: template.id,
                stepNo: step.stepNo,
                Name: step.Name,
                stepType: step.stepType,
                approverIds: extractApproverIds(step.approvers || []),
                departmentTitles: extractDepartmentTitles(step.approvers || [], (step as any).departmentTitles),
                departmentTitleIds: extractDepartmentTitleIds(step.approvers || [], (step as any).departmentTitleIds || (step as any).DepartmentTitleIds),
                DepartmentTitleIds: extractDepartmentTitleIds(step.approvers || [], (step as any).departmentTitleIds || (step as any).DepartmentTitleIds),
                approvers: step.approvers || [],
                approvalRule: step.approvalRule,
                timeLimit: step.timeLimit || 0,
                description: step.description,
                isOptional: step.isOptional
              })
            }
          )
        )
      );
    }

    return fetchTemplate(template.id || '');
  } catch (error) {
    console.error('Error updating template:', error);
    throw error;
  }
}

/**
 * Delete template and its steps
 */
export async function deleteTemplate(templateId: string): Promise<void> {
  try {
    // Delete all steps
    const stepsResponse = await fetchUtils.fetchJson(
      `${stepUrl}/byTemplate/${templateId}`,
      { headers: getHeaders() }
    );
    
    const steps = stepsResponse.json.data || stepsResponse.json || [];
    
    await Promise.all(
      steps.map((step: ApprovalStep) =>
        fetchUtils.fetchJson(
          `${stepUrl}/${step.id}`,
          {
            method: 'DELETE',
            headers: getHeaders()
          }
        )
      )
    );

    // Delete template
    await fetchUtils.fetchJson(
      `${templateUrl}/${templateId}`,
      {
        method: 'DELETE',
        headers: getHeaders()
      }
    );
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
}
